import React, { useState } from 'react';
import Header from './Header';
import ProjectForm from './ProjectForm';
import FilterBar from './FilterBar';
import StallCard from './StallCard';
import PreviewModal from './PreviewModal';
import ClientsPage from './ClientsPage';
import EventCoveragePage from './EventCoveragePage';
import TechPage from './TechPage';
import ImageBot from './ImageBot';
import STALLS from '../data/stalls';

function Dashboard({ user, onLogout }) {
  // Project form state
  const [clientName, setClientName] = useState('');
  const [exhibition, setExhibition] = useState('');
  const [location, setLocation] = useState('');
  const [eventDateFrom, setEventDateFrom] = useState('');
  const [eventDateTo, setEventDateTo] = useState('');

  // Filter state
  const [areaFilter, setAreaFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  // UI state
  const [filtered, setFiltered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clients, setClients] = useState([]);
  const [showClients, setShowClients] = useState(false);
  const [showEventCoverage, setShowEventCoverage] = useState(false);
  const [showTech, setShowTech] = useState(false);

  // Load persisted clients on mount
  React.useEffect(() => {
    fetch('/clients')
      .then((r) => r.json())
      .then((data) => setClients(data))
      .catch(() => {});
  }, []);

  const handleSelectDesign = async (stall, logos) => {
    const newClient = {
      clientName,
      exhibition,
      location,
      eventDateFrom,
      eventDateTo,
      stall,
      logos: logos || [],
      savedAt: Date.now(),
    };
    try {
      const res = await fetch('/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClient),
      });
      const data = await res.json();
      setClients((prev) => [...prev, { ...newClient, id: data.id }]);
    } catch {
      // server offline — keep in memory only
      setClients((prev) => [...prev, newClient]);
    }
    setSelected(null);
    setShowClients(true);
  };

  const handleDeleteClient = (id) => {
    setClients((prev) => prev.filter((c) => c.id !== id));
  };

  const applyFilters = async () => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));
    let res = STALLS;
    if (areaFilter) res = res.filter((s) => s.area <= +areaFilter);
    if (sizeFilter) res = res.filter((s) => s.size === sizeFilter);
    if (typeFilter) res = res.filter((s) => s.type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      res = res.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q) ||
          s.desc.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q)
      );
    }
    setFiltered(res);
    setLoading(false);
  };

  const clearFilters = () => {
    setAreaFilter('');
    setSizeFilter('');
    setTypeFilter('');
    setSearch('');
    setFiltered(null);
  };

  const hasFilters = !!(areaFilter || sizeFilter || typeFilter || search || filtered);
  const displayStalls = filtered ?? STALLS;

  if (showClients) {
    return (
      <>
        <ClientsPage clients={clients} onBack={() => setShowClients(false)} onDeleteClient={handleDeleteClient} />
        <ImageBot />
      </>
    );
  }

  if (showEventCoverage) {
    return (
      <>
        <EventCoveragePage onBack={() => setShowEventCoverage(false)} user={user} onLogout={onLogout} />
        <ImageBot />
      </>
    );
  }

  if (showTech) {
    return (
      <>
        <TechPage onBack={() => setShowTech(false)} user={user} onLogout={onLogout} />
        <ImageBot />
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F7F5F2' }}>
      <Header
        user={user}
        onLogout={onLogout}
        clientCount={clients.length}
        onViewClients={() => setShowClients(true)}
        onViewEventCoverage={() => setShowEventCoverage(true)}
        onViewTech={() => setShowTech(true)}
      />

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 2rem' }}>
        {/* Page title */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h1
            style={{
              fontFamily: 'DM Serif Display',
              fontSize: 36,
              color: '#1a1a1a',
              marginBottom: 6,
            }}
          >
            Exhibition Planner
          </h1>
          <p style={{ color: '#888', fontSize: 14 }}>
            Fill in the details below and explore our curated stall design library.
          </p>
        </div>

        {/* Project form */}
        <ProjectForm
          clientName={clientName} setClientName={setClientName}
          exhibition={exhibition} setExhibition={setExhibition}
          location={location} setLocation={setLocation}
          eventDateFrom={eventDateFrom} setEventDateFrom={setEventDateFrom}
          eventDateTo={eventDateTo} setEventDateTo={setEventDateTo}
        />

        {/* Filter bar */}
        <FilterBar
          areaFilter={areaFilter} setAreaFilter={setAreaFilter}
          sizeFilter={sizeFilter} setSizeFilter={setSizeFilter}
          typeFilter={typeFilter} setTypeFilter={setTypeFilter}
          search={search} setSearch={setSearch}
          onApply={applyFilters}
          onClear={clearFilters}
          hasFilters={hasFilters}
        />

        {/* Gallery header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'DM Serif Display',
                fontSize: 26,
                color: '#1a1a1a',
                marginBottom: 2,
              }}
            >
              Stall Designs
            </h2>
            <p style={{ fontSize: 13, color: '#aaa' }}>
              {loading
                ? 'Searching…'
                : `${displayStalls.length} design${displayStalls.length !== 1 ? 's' : ''} available`}
            </p>
          </div>
          {filtered && (
            <div
              style={{
                background: '#EEEdFF',
                borderRadius: 7,
                padding: '4px 12px',
                fontSize: 12,
                color: '#5B4FCF',
                fontWeight: 500,
              }}
            >
              Filtered Results
            </div>
          )}
        </div>

        {/* Gallery content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#aaa' }}>
            <div
              style={{
                fontSize: 32,
                marginBottom: 12,
                display: 'inline-block',
                animation: 'spin 1.2s linear infinite',
              }}
            >
              ◌
            </div>
            <p style={{ fontSize: 14 }}>Searching designs…</p>
          </div>
        ) : displayStalls.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '5rem 2rem',
              background: '#fff',
              borderRadius: 18,
              border: '1px solid #ede9e3',
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>◎</div>
            <h3
              style={{
                fontFamily: 'DM Serif Display',
                fontSize: 22,
                color: '#1a1a1a',
                marginBottom: 8,
              }}
            >
              No designs found
            </h3>
            <p style={{ color: '#aaa', fontSize: 14, marginBottom: 20 }}>
              Try adjusting your filters or clearing the search.
            </p>
            <button
              onClick={clearFilters}
              style={{
                background: '#1a1a1a',
                border: 'none',
                borderRadius: 9,
                padding: '10px 24px',
                color: '#fff',
                fontSize: 13,
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div
            className="fade-in"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            {displayStalls.map((s) => (
              <StallCard key={s.id} stall={s} onClick={setSelected} />
            ))}
          </div>
        )}
      </main>

      {/* Preview modal */}
      {selected && <PreviewModal stall={selected} onClose={() => setSelected(null)} onSelect={handleSelectDesign} />}

      <ImageBot />
    </div>
  );
}

export default Dashboard;
